// ConfirmDeleteModal.tsx
import React from 'react';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  configurationName: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** What is being deleted, for the prompt — "configuration" by default. */
  itemLabel?: string;
  title?: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  isDestructive?: boolean;
  /** Disables both buttons — use while the confirm action is in flight to prevent a double-submit. */
  confirmDisabled?: boolean;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  configurationName,
  onConfirm,
  onCancel,
  itemLabel = 'configuration',
  title = 'Confirm Deletion',
  message,
  confirmLabel,
  cancelLabel,
  isDestructive = false,
  confirmDisabled = false,
}) => {
  if (!isOpen) return null;

  const defaultConfirmText = confirmLabel || 'Yes';
  const defaultCancelText = cancelLabel || 'No';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        {message ? (
          <div className="mb-6 break-words text-gray-700 text-sm leading-relaxed">{message}</div>
        ) : (
          <p className="mb-6 break-words">
            Are you sure you want to delete this {itemLabel}
            <br />
            <strong className="break-all">"{configurationName}"</strong> ?
          </p>
        )}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={onCancel}
            disabled={confirmDisabled}
          >
            {defaultCancelText}
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 font-medium text-sm text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
              isDestructive
                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                : 'bg-[#FFD700] hover:bg-[#E6C200] focus:ring-[#F5C400]'
            }`}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {defaultConfirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDeleteModal;